import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Menu, X, Sun, Moon, Globe, Play } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import Lenis from 'lenis';

const languages = [
  { code: 'sq', name: 'Shqip', flag: '🇦🇱' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' }
];

const PublicLayout = ({ children }) => {
  const { theme, toggleTheme, language, setLanguage, t, settings } = useApp();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const location = useLocation();
  const lenisRef = useRef(null);

  // Initialize smooth scroll
  useEffect(() => {
    lenisRef.current = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true
    });

    function raf(time) {
      lenisRef.current?.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenisRef.current?.destroy();
    };
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    lenisRef.current?.scrollTo(0, { immediate: true });
    setMenuOpen(false);
  }, [location.pathname]);

  // Reveal animation on scroll
  useEffect(() => {
    if (!settings?.theme_settings?.scroll_reveal) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [settings, location.pathname]);

  const navLinks = [
    { path: '/', label: t('nav.home', 'Home') },
    { path: '/releases', label: t('nav.releases', 'Releases') },
    { path: '/videos', label: t('nav.videos', 'Videos') },
    { path: '/about', label: t('nav.about', 'About') },
    { path: '/contact', label: t('nav.contact', 'Contact') }
  ];

  const currentLang = languages.find(l => l.code === language) || languages[2];

  return (
    <div className="min-h-screen bg-background">
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <nav className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-2 group"
              data-testid="logo-link"
            >
              <div className="w-10 h-10 rounded-full bg-brand-red flex items-center justify-center group-hover:glow-red transition-all duration-300">
                <Play className="w-5 h-5 text-white fill-white" />
              </div>
              <span className="font-syne font-bold text-xl tracking-tight text-white hidden sm:block">
                GOLDENG
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                  className={`relative font-manrope text-sm uppercase tracking-wider px-4 py-2 transition-all duration-300 hover:text-brand-gold ${
                    location.pathname === link.path 
                      ? 'text-white' 
                      : 'text-white/60'
                  }`}
                >
                  {link.label}
                  {/* Gold underline for active state */}
                  <span className={`absolute bottom-0 left-4 right-4 h-0.5 bg-brand-gold transform transition-transform duration-300 origin-left ${
                    location.pathname === link.path ? 'scale-x-100' : 'scale-x-0'
                  }`} />
                </Link>
              ))}
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-2">
              {/* Language Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-white/70 hover:text-white"
                    data-testid="language-switcher"
                  >
                    <Globe className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="glass">
                  {languages.map(lang => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={`cursor-pointer ${language === lang.code ? 'text-brand-red' : ''}`}
                      data-testid={`lang-${lang.code}`}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="text-white/70 hover:text-white"
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMenuOpen(!menuOpen)}
                className="md:hidden text-white/70 hover:text-white"
                data-testid="mobile-menu-toggle"
              >
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden glass border-t border-white/5 animate-slide-in">
            <div className="px-4 py-6 space-y-4">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                  className={`block font-manrope text-lg ${
                    location.pathname === link.path ? 'text-brand-red' : 'text-white/70'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-16 md:pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-black/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {/* Brand */}
            <div>
              <Link to="/" className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-full bg-brand-red flex items-center justify-center">
                  <Play className="w-5 h-5 text-white fill-white" />
                </div>
                <span className="font-syne font-bold text-xl text-white">GOLDENG</span>
              </Link>
              <p className="text-white/50 text-sm">
                {settings?.footer_text?.[language] || 'Music Producer & Artist'}
              </p>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="font-syne font-semibold text-white mb-4">Navigation</h4>
              <div className="space-y-2">
                {navLinks.map(link => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="block text-white/50 hover:text-brand-red text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="font-syne font-semibold text-white mb-4">Connect</h4>
              <div className="flex flex-wrap gap-4">
                {settings?.social_links?.youtube && (
                  <a 
                    href={settings.social_links.youtube} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white/50 hover:text-brand-red transition-colors"
                    data-testid="social-youtube"
                  >
                    YouTube
                  </a>
                )}
                {settings?.social_links?.instagram && (
                  <a 
                    href={settings.social_links.instagram} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white/50 hover:text-brand-red transition-colors"
                    data-testid="social-instagram"
                  >
                    Instagram
                  </a>
                )}
                {settings?.social_links?.spotify && (
                  <a 
                    href={settings.social_links.spotify} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white/50 hover:text-brand-red transition-colors"
                    data-testid="social-spotify"
                  >
                    Spotify
                  </a>
                )}
                {settings?.social_links?.tiktok && (
                  <a 
                    href={settings.social_links.tiktok} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white/50 hover:text-brand-red transition-colors"
                    data-testid="social-tiktok"
                  >
                    TikTok
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-12 pt-8 border-t border-white/5 text-center">
            <p className="text-white/30 text-sm">
              © {new Date().getFullYear()} GoldenG Music. {t('footer.rights', 'All rights reserved')}.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
